import React, {useEffect, useState} from 'react';
import moment from 'moment';
import {Formik} from 'formik';
import {useRouteMatch} from 'react-router-dom';
import SilentSubmitHandler from '../SilentSubmitHandler';
import ModalPreviewDocument from '../../../../common/components/ModalPreviewDocument';
import Form_8View from './Form_8-view';
import {normalizeFormValues} from 'common/utils/form/normalizeFormValues';
import {pathnames as P} from 'common/constants/pathnames';
import {isBankShumaTypeID} from '../../../../common/constants/shumaType';
import ButtonSubmitForm from '../ButtonSubmitForm';
import SpinnerContainer from '../../../../common/components/SpinnerContainer';
import {isFileUploaded} from "../../utils/isFileUploaded";
import {fileTypeId as T} from "../../constants/fileTypeId";
import {uploadingProgress} from "../../../../common/utils/form/uploadingProgress";

const Form_8 = ({
                  stepData,
                  submitStep,
                  getShumaStepStatus,
                  showGlobalErrorMessage,
                  shumaTypeID,
                  getStepData,
                  uploadFile,
                  getFileList,
                  filesListData
                }) => {
  useEffect(() => {
    getFileList({shumaId})
  }, []);
  const [reinit, setReinit] = useState(true);
  const [submit, setSubmit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showUploadingFilePopUp, setShowUploadingFilePopUp] = useState(false);
  const [progress, setProgress] = useState(0);
  const {
    officialDate,
    cityName,
    housePermitArea,
    bankName,
    ...rest
  } = stepData;

  const initialValues = {
    propertyLocation: '',
    propertyProperties: '',
    officialDate: officialDate ? moment(officialDate).toDate() : null,
    houseRegisteredArea: '',
    planningBackground: '',
    possessoryBackground: '',
    areaPropertiesPrices: '',
    other: '',
    additionalStatements: '',
    fileType36Switch: false,
    ...rest
  };
  const notesFileUploaded = isFileUploaded({fileList: filesListData, fileTypeID: T.NOTES});
  const {params: {shumaId, formId: stepId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const submitWithoutFormReinitialization = values => {
    const {
      propertyLocation,
      propertyProperties,
      planningBackground,
      possessoryBackground,
      areaPropertiesPrices,
      other,
      additionalStatements,
      notes
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      shumaTypeID,
      propertyLocation,
      propertyProperties,
      planningBackground,
      possessoryBackground,
      areaPropertiesPrices,
      other,
      additionalStatements,
      notes
    };
    setReinit(false);  //https://github.com/formium/formik/issues/2369
    return submitStep({step: 8, data})
      .then(() => setReinit(true))
  };

  useEffect(() => {
    setSubmit(false)
  }, [])

  const handleDataSubmit = (values, shumaId) => {
    if (!submit) {
      submitWithoutFormReinitialization(values)
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleDataSubmitForPreview = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleDataSubmitForSave = (values, shumaId, dirty) => {
    if (dirty) {
      setLoading(true);
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleFileUpload = formikProps => fileTypeId => (file, payload) => {
    setShowUploadingFilePopUp(true);
    submitFiles(fileTypeId)(file, payload)
      .then(() => uploadingProgress(setProgress))
      .then(() => submitWithoutFormReinitialization(formikProps.values))
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
      .finally(() => {
        setShowUploadingFilePopUp(false);
        getFileList({shumaId});
        getStepData({shumaId, stepId});
      });
  };

  const submitFiles = fileTypeId => (fileToSave, payload) => {
    return uploadFile({shumaId, filename: fileToSave.name, fileTypeId, fileToSave}, payload);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {
      }}
      enableReinitialize={reinit}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {(formikProps) => (
        <>
          <SilentSubmitHandler
            onSubmit={handleDataSubmit}
            values={formikProps.values}
            shumaId={shumaId}
            dirty={formikProps.dirty}
          />
          <ButtonSubmitForm
            onClick={() => {
              handleDataSubmitForSave(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
            disabled={!formikProps.dirty}
          />
          <ModalPreviewDocument
            submitOnOpen={() => {
              handleDataSubmitForPreview(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
          />
          {loading ? <SpinnerContainer/> : (
            <Form_8View
              {...formikProps}
              shumaID={shumaId}
              shumaTypeID={shumaTypeID}
              cityName={cityName}
              bankName={bankName}
              prevPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, isBankShumaTypeID(shumaTypeID) ? 12 : 10)}
              nextPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 11)}
              onFileSelected={handleFileUpload(formikProps)}
              fileTypeIds={{
                notes: T.NOTES
              }}
              fileUploaded={{
                notesFileUploaded
              }}
              progress={progress}
              showUploadingFilePopUp={showUploadingFilePopUp}
              filesListData={filesListData ?? []}
            />
          )}
        </>
      )}
    </Formik>
  )
};

export default Form_8;